"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  isAdminRole,
} from "../constants/roles";
import { getDefaultPermissionsByRole } from "../constants/sidebarTabs";
import { permissionService } from "../apis/permissionService";
import {
  DollarSign,
  Users,
  Briefcase,
  Receipt,
  Gift,
  CreditCard,
  Settings,
  User,
  ChevronDown,
  Coffee,
  BarChart,
  BarChart2,
  Clock,
  Coins,
  CoinsIcon,
  CreditCardIcon,
  Trophy,
  MessageSquare,
  Wine,
  UserCheck,
  Sparkles,
  Utensils,
  Percent,
  Pin,
  PinOff,
} from "lucide-react";

export function AppSidebar() {
  const { currentUser } = useAuth();
  const location = useLocation();

  // Pin state: allows user to keep sidebar permanently expanded if desired
  const [isPinned, setIsPinned] = useState(() => {
    try {
      return localStorage.getItem("sidebar_pinned") === "true";
    } catch {
      return false;
    }
  });

  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = isPinned || isHovered;

  // Track accordion open/closed state
  const [openSubmenus, setOpenSubmenus] = useState({
    "Tips Management": false,
    "Payroll Management": false,
  });

  // User role resolution
  const userRole = useMemo(() => {
    if (currentUser?.role) return currentUser.role;
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored)?.role : null;
    } catch {
      return null;
    }
  }, [currentUser]);

  const isAdmin = isAdminRole(userRole);

  // Dynamic user tab permissions state
  const [activePermissions, setActivePermissions] = useState(() => {
    if (currentUser?.permissions && Object.keys(currentUser.permissions).length > 0) {
      return currentUser.permissions;
    }
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored)?.permissions : null;
    } catch {
      return null;
    }
  });

  // Sync permissions from API on mount & on custom permissions_updated event
  useEffect(() => {
    let isMounted = true;

    const loadLivePermissions = async () => {
      // Admin always has full access
      if (isAdmin) return;

      try {
        let storedUser = null;
        try {
          const raw = localStorage.getItem("user");
          storedUser = raw ? JSON.parse(raw) : null;
        } catch (_) {}

        const userId = currentUser?.userid || storedUser?.userid || currentUser?.id || storedUser?.id;
        const roleId = currentUser?.roleid || storedUser?.roleid;

        if (userId) {
          const res = await permissionService.getUserPermissions(userId);
          const perms = res.data?.permissions || (res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : null);
          if (isMounted && res.success && perms) {
            setActivePermissions(perms);
            if (storedUser) {
              storedUser.permissions = perms;
              localStorage.setItem("user", JSON.stringify(storedUser));
            }
            return;
          }
        }

        if (roleId) {
          const res = await permissionService.getRolePermissions(roleId);
          const perms = res.data?.permissions || (res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : null);
          if (isMounted && res.success && perms) {
            setActivePermissions(perms);
            if (storedUser) {
              storedUser.permissions = perms;
              localStorage.setItem("user", JSON.stringify(storedUser));
            }
          }
        }
      } catch (err) {
        console.error("Error loading live sidebar permissions:", err);
      }
    };

    loadLivePermissions();

    const handlePermUpdate = () => {
      loadLivePermissions();
    };

    window.addEventListener("permissions_updated", handlePermUpdate);
    window.addEventListener("storage", handlePermUpdate);

    return () => {
      isMounted = false;
      window.removeEventListener("permissions_updated", handlePermUpdate);
      window.removeEventListener("storage", handlePermUpdate);
    };
  }, [isAdmin, currentUser]);

  const canAccessTab = useCallback(
    (tabKey) => {
      if (!tabKey) return true;
      if (isAdmin) return true;
      // If permissions object loaded and populated, check it directly
      if (activePermissions && typeof activePermissions === "object" && Object.keys(activePermissions).length > 0) {
        return activePermissions[tabKey] === true;
      }
      // Safe fallback based on role while permissions are syncing
      const defaultPerms = getDefaultPermissionsByRole(userRole);
      return defaultPerms[tabKey] === true;
    },
    [isAdmin, activePermissions, userRole]
  );

  // Submenu route matchers
  const isTipPath = useMemo(() => {
    const p = location.pathname;
    return [
      "/dealer-tips",
      "/waitress-tips",
      "/floor-tips",
      "/floor-supervisor-tips",
      "/bartender-tips",
      "/host-tips",
      "/cashier-tips",
      "/janitor-tips",
      "/cook-tips",
      "/other-tips",
    ].some((route) => p === route || p.startsWith(`${route}/`));
  }, [location.pathname]);

  const isPayrollPath = useMemo(() => {
    return location.pathname.startsWith("/dashboard/payroll");
  }, [location.pathname]);

  // Auto-expand accordion when navigating to a child page
  useEffect(() => {
    if (isTipPath) {
      setOpenSubmenus((prev) => ({ ...prev, "Tips Management": true }));
    }
    if (isPayrollPath) {
      setOpenSubmenus((prev) => ({ ...prev, "Payroll Management": true }));
    }
  }, [isTipPath, isPayrollPath]);

  const toggleSubmenu = useCallback((title) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  }, []);

  const handleTogglePin = useCallback(() => {
    setIsPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sidebar_pinned", String(next));
      } catch (e) {
        console.error("Error saving sidebar pin preference:", e);
      }
      return next;
    });
  }, []);

  // 1. Leaderboard category
  const leaderboardItems = useMemo(
    () =>
      [
        {
          title: "Tournament Leaderboard",
          icon: Trophy,
          href: "/tournament-leaderboard",
          tabKey: "tournament_leaderboard",
        },
      ].filter((item) => canAccessTab(item.tabKey)),
    [canAccessTab]
  );

  // 2. Management category
  const managementItems = useMemo(() => {
    const rawPayrollSubItems = [
      { title: "Sales Analytics", href: "/dashboard/payroll/sales-analytics", icon: BarChart2, tabKey: "payroll_sales_analytics" },
      { title: "Payroll Settings", href: "/dashboard/payroll/salary", icon: Settings, tabKey: "payroll_salary" },
      { title: "Payroll Entry", href: "/dashboard/payroll/bonuses", icon: Gift, tabKey: "payroll_bonuses" },
    ].filter((s) => canAccessTab(s.tabKey));

    const items = [
      {
        title: "Analytics Overview",
        icon: BarChart,
        href: "/",
        tabKey: "analytics_overview",
      },
      {
        title: "User Management",
        icon: Users,
        href: "/user-management",
        tabKey: "user_management",
      },
      {
        title: "Employee Management",
        icon: Briefcase,
        href: "/employee-management",
        tabKey: "employee_management",
      },
      ...(rawPayrollSubItems.length > 0
        ? [
            {
              title: "Payroll Management",
              icon: Receipt,
              href: "/dashboard/payroll",
              hasSubmenu: true,
              subItems: rawPayrollSubItems,
            },
          ]
        : []),
      {
        title: "Token Reserves",
        icon: Coins,
        href: "/token-reserves",
        tabKey: "token_reserves",
      },
      {
        title: "Starting/Closing Tables Time",
        icon: Clock,
        href: "/starting-closing-tables-time",
        tabKey: "tables_time",
      },
      {
        title: "Tax Management",
        icon: Percent,
        href: "/tax-management",
        tabKey: "tax_management",
      },
    ];

    return items.filter((item) => {
      if (item.hasSubmenu) return true;
      return canAccessTab(item.tabKey);
    });
  }, [canAccessTab]);

  // 3. Transactions category
  const transactionItems = useMemo(() => {
    const tipSubItems = [
      { title: "Dealer Tips", href: "/dealer-tips", icon: Users, tabKey: "tips_dealer" },
      { title: "Server/waitress Tips", href: "/waitress-tips", icon: Coffee, tabKey: "tips_waitress" },
      { title: "Floor Tips", href: "/floor-tips", icon: Gift, tabKey: "tips_floor" },
      { title: "Bartender Tips", href: "/bartender-tips", icon: Wine, tabKey: "tips_bartender" },
      { title: "Host Tips", href: "/host-tips", icon: UserCheck, tabKey: "tips_host" },
      { title: "Cashier Tips", href: "/cashier-tips", icon: Receipt, tabKey: "tips_cashier" },
      { title: "Custodian/Janitor Tips", href: "/janitor-tips", icon: Sparkles, tabKey: "tips_janitor" },
      { title: "Cook Tips", href: "/cook-tips", icon: Utensils, tabKey: "tips_cook" },
      { title: "Other Tips", href: "/other-tips", icon: DollarSign, tabKey: "tips_other" },
    ].filter((s) => canAccessTab(s.tabKey));

    const items = [
      ...(tipSubItems.length > 0
        ? [
            {
              title: "Tips Management",
              icon: DollarSign,
              href: "/tips",
              hasSubmenu: true,
              subItems: tipSubItems,
            },
          ]
        : []),
      { title: "Promos", icon: Gift, href: "/promos", tabKey: "promos" },
      { title: "Petty Cash", icon: CreditCard, href: "/petty-cash", tabKey: "petty_cash" },
      { title: "Poker Cash Box", icon: CreditCardIcon, href: "/poker-cash-box", tabKey: "poker_cash_box" },
      { title: "Tournament Poker Chips", icon: CoinsIcon, href: "/tournament-poker-chips", tabKey: "tournament_poker_chips" },
      { title: "Tournament House Fees", icon: BarChart2, href: "/tournament-house-fees", tabKey: "tournament_house_fees" },
    ];

    return items.filter((item) => {
      if (item.hasSubmenu) return true;
      return canAccessTab(item.tabKey);
    });
  }, [canAccessTab]);

  // 4. Table Tracker category
  const tableTrackerItems = useMemo(
    () =>
      [
        {
          title: "Player Profiles",
          icon: User,
          href: "/player-profiles",
          tabKey: "player_profiles",
        },
        {
          title: "SMS",
          icon: MessageSquare,
          href: "/sms-logs",
          tabKey: "sms_logs",
        },
      ].filter((item) => canAccessTab(item.tabKey)),
    [canAccessTab]
  );

  // Renders a navigation item (link or click-accordion)
  const renderItem = (item) => {
    const hasSubmenu = Boolean(item.hasSubmenu && item.subItems?.length);

    if (hasSubmenu) {
      const isOpen = Boolean(openSubmenus[item.title]);
      const isParentActive =
        item.title === "Tips Management" ? isTipPath : isPayrollPath;

      return (
        <div key={item.title} className="relative">
          <button
            type="button"
            onClick={() => {
              if (!isExpanded) {
                setIsHovered(true);
              }
              toggleSubmenu(item.title);
            }}
            title={!isExpanded ? item.title : undefined}
            className={`group relative flex items-center w-full transition-all duration-200 rounded-lg text-[15px] font-medium ${
              isExpanded
                ? "justify-between px-3.5 py-2.5"
                : "justify-center px-0 py-2.5"
            } ${
              isParentActive
                ? "bg-gray-100 text-gray-900 font-semibold"
                : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            }`}
          >
            <div className="flex items-center space-x-3 min-w-0">
              <item.icon
                size={20}
                strokeWidth={isParentActive ? 2 : 1.75}
                className={`shrink-0 transition-colors ${
                  isParentActive
                    ? "text-gray-900"
                    : "text-gray-500 group-hover:text-gray-800"
                }`}
              />
              {isExpanded && (
                <span className="truncate text-left">{item.title}</span>
              )}
            </div>

            {isExpanded && (
              <div className="flex items-center space-x-1.5 shrink-0 ml-2">
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                  {item.subItems.length}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-gray-400 transition-transform duration-200 ${
                    isOpen ? "rotate-180 text-gray-700" : ""
                  }`}
                />
              </div>
            )}

            {/* Hover tooltip when sidebar is collapsed */}
            {!isExpanded && (
              <div className="pointer-events-none absolute left-full ml-3 hidden rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white shadow-lg group-hover:block z-50 whitespace-nowrap">
                {item.title} ({item.subItems.length})
              </div>
            )}
          </button>

          {/* Instant Inline Accordion Submenu */}
          {isExpanded && isOpen && (
            <div className="mt-1 ml-4 pl-3 border-l-2 border-gray-200 space-y-0.5 py-1 animate-in fade-in-50 duration-150">
              {item.subItems.map((subItem) => {
                const isSubActive =
                  location.pathname === subItem.href ||
                  (subItem.href === "/floor-tips" &&
                    location.pathname === "/floor-supervisor-tips");
                const SubIcon = subItem.icon;

                return (
                  <Link
                    key={subItem.href}
                    to={subItem.href}
                    className={`group flex items-center space-x-2.5 px-3 py-1.5 rounded-md text-[13.5px] font-medium transition-colors ${
                      isSubActive
                        ? "bg-gray-900 text-white font-medium shadow-sm"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    {SubIcon && (
                      <SubIcon
                        size={15}
                        strokeWidth={isSubActive ? 2 : 1.75}
                        className={`shrink-0 ${
                          isSubActive
                            ? "text-white"
                            : "text-gray-400 group-hover:text-gray-700"
                        }`}
                      />
                    )}
                    <span className="truncate">{subItem.title}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const isActive =
      item.href === "/"
        ? location.pathname === "/"
        : location.pathname === item.href ||
          location.pathname.startsWith(`${item.href}/`);

    return (
      <Link
        key={item.href}
        to={item.href}
        title={!isExpanded ? item.title : undefined}
        className={`group relative flex items-center transition-all duration-200 rounded-lg text-[15px] font-medium ${
          isExpanded
            ? "justify-between px-3.5 py-2.5"
            : "justify-center px-0 py-2.5"
        } ${
          isActive
            ? "bg-gray-900 text-white shadow-sm font-semibold"
            : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
        }`}
      >
        <div className="flex items-center space-x-3 min-w-0">
          <item.icon
            size={20}
            strokeWidth={isActive ? 2 : 1.75}
            className={`shrink-0 transition-colors ${
              isActive
                ? "text-white"
                : "text-gray-500 group-hover:text-gray-800"
            }`}
          />
          {isExpanded && <span className="truncate">{item.title}</span>}
        </div>

        {/* Hover tooltip when sidebar is collapsed */}
        {!isExpanded && (
          <div className="pointer-events-none absolute left-full ml-3 hidden rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white shadow-lg group-hover:block z-50 whitespace-nowrap">
            {item.title}
          </div>
        )}
      </Link>
    );
  };

  const renderSectionHeader = (title, Icon, count) => {
    if (isExpanded) {
      return (
        <div className="mx-0.5 mt-4 mb-2.5 px-3 py-2 rounded-lg bg-gradient-to-r from-gray-800 via-gray-750 to-gray-700 text-white shadow-sm flex items-center justify-between border-l-4 border-yellow-500">
          <div className="flex items-center space-x-2">
            {Icon && <Icon size={15} className="text-yellow-400 shrink-0" />}
            <span className="font-bold text-[13px] tracking-wider uppercase text-white">
              {title}
            </span>
          </div>
          {typeof count === "number" && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-gray-700 text-gray-200">
              {count}
            </span>
          )}
        </div>
      );
    }
    return (
      <div className="my-3 mx-auto flex flex-col items-center justify-center" title={title}>
        <div className="w-8 border-t-2 border-gray-300" />
      </div>
    );
  };

  return (
    <aside
      className={`bg-white shadow-xl flex flex-col h-[calc(100vh-4rem)] fixed top-16 left-0 transition-all duration-300 ease-in-out ${
        isExpanded ? "w-[285px]" : "w-[76px]"
      } border-r border-gray-200 z-40 select-none`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top Sidebar Bar with Pin/Unpin Toggle */}
      <div
        className={`flex items-center ${
          isExpanded ? "justify-between px-4" : "justify-center px-2"
        } py-2.5 border-b border-gray-100 bg-gray-50/60 shrink-0`}
      >
        {isExpanded && (
          <span className="text-xs font-bold tracking-wider text-gray-500 uppercase">
            Navigation
          </span>
        )}
        <button
          type="button"
          onClick={handleTogglePin}
          title={
            isPinned
              ? "Unpin sidebar (hover to expand)"
              : "Pin sidebar permanently open"
          }
          className={`p-1.5 rounded-md transition-colors ${
            isPinned
              ? "bg-gray-200 text-gray-800 hover:bg-gray-300"
              : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          {isPinned ? <Pin size={16} /> : <PinOff size={16} />}
        </button>
      </div>

      {/* Nav List with slim scrollbar */}
      <div
        className="flex-1 overflow-y-auto px-2.5 pb-8 space-y-2"
        style={{ scrollbarWidth: "thin" }}
      >
        {/* Category 1: LEADERBOARD */}
        {leaderboardItems.length > 0 && (
          <section className="pb-3 border-b border-gray-100">
            {renderSectionHeader("Leaderboard", Trophy, leaderboardItems.length)}
            <nav className="space-y-1">{leaderboardItems.map(renderItem)}</nav>
          </section>
        )}

        {/* Category 2: MANAGEMENT */}
        {managementItems.length > 0 && (
          <section className="pb-3 border-b border-gray-100">
            {renderSectionHeader("Management", Briefcase, managementItems.length)}
            <nav className="space-y-1">{managementItems.map(renderItem)}</nav>
          </section>
        )}

        {/* Category 3: TRANSACTIONS */}
        {transactionItems.length > 0 && (
          <section className="pb-3 border-b border-gray-100">
            {renderSectionHeader("Transactions", DollarSign, transactionItems.length)}
            <nav className="space-y-1">{transactionItems.map(renderItem)}</nav>
          </section>
        )}

        {/* Category 4: TABLE TRACKER */}
        {tableTrackerItems.length > 0 && (
          <section className="pb-3">
            {renderSectionHeader("Table Tracker", Users, tableTrackerItems.length)}
            <nav className="space-y-1">{tableTrackerItems.map(renderItem)}</nav>
          </section>
        )}
      </div>
    </aside>
  );
}